import { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiBell } from 'react-icons/fi';
import PropertyFilters from '../components/filtros/PropertyFilters';
import PropertyList from '../components/PropertyList';
import { apiRequest } from '../services/api';
import { trackProductEvent } from '../services/productAnalytics';
import { useAuth } from '../auth/AuthContext';

const FILTER_KEYS = [
  'operation', 'type', 'province', 'department', 'neighborhood', 'location',
  'minPrice', 'maxPrice', 'rooms', 'bedrooms', 'bathrooms', 'minSurface', 'maxSurface', 'garage',
  'isAptoCredito', 'condition', 'currency', 'feature', 'sort',
  'petsAllowed', 'pool', 'quincho', 'garden', 'terrace', 'balcony', 'privateNeighborhood',
];

export default function Buscar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alertState, setAlertState] = useState('');

  const filters = useMemo(() => FILTER_KEYS.reduce((result, key) => {
    const value = searchParams.get(key);
    if (value !== null && value !== '') {
      result[key] = value === 'true' ? true : value;
    }
    return result;
  }, {}), [searchParams]);

  const search = searchParams.get('q') || '';

  const handleSearchChange = useCallback((value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === undefined || value === null || String(value).trim() === '') next.delete('q');
      else next.set('q', String(value).trim());
      return next;
    }, { replace:true });
  }, [setSearchParams]);

  const handleChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextValue === undefined || nextValue === null || nextValue === '' || nextValue === false) next.delete(name);
      else next.set(name, String(nextValue));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleLocationSelect = useCallback((item) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      ['province','department','neighborhood','location'].forEach((key) => next.delete(key));
      const label = item?.name || item?.value || '';
      if (label) next.set('location', label);
      if (item?.province) next.set('province', item.province);
      if (item?.type === 'department') next.set('department', item.value || item.name);
      if (item?.type === 'neighborhood') {
        next.set('neighborhood', item.value || item.name);
        if (item.department) next.set('department', item.department);
      }
      return next;
    }, { replace:true });
  }, [setSearchParams]);

  const saveAlert = async () => {
    if (!user) return navigate('/login', { state:{ from:{ pathname:`/buscar?${searchParams}` } } });
    const alertFilters = { ...filters, ...(search ? { q:search } : {}) };
    if (!Object.keys(alertFilters).length) return setAlertState('Elegí al menos un filtro');
    setAlertState('Guardando…');
    try {
      await apiRequest('/api/users/me/alerts/searches', { method:'POST', body:{ filters:alertFilters, frequency:'DAILY' } });
      trackProductEvent('ALERT_CREATED',{metadata:{filters:Object.keys(alertFilters).join(',')}});
      setAlertState('Alerta activada');
    } catch (error) { setAlertState(error.message); }
  };

  if (filters.operation === 'alquiler_vacacional') {
    const vacationParams = new URLSearchParams(searchParams);
    vacationParams.delete('operation');
    return <Navigate replace to={`/vacaciones${vacationParams.size ? `?${vacationParams}` : ''}`} />;
  }

  return (
    <Page>
      <PropertyFilters
        filters={filters}
        search={search}
        onSearchChange={handleSearchChange}
        onChange={handleChange}
        onLocationSelect={handleLocationSelect}
      />
      <AlertSection>
        <AlertBar>
          <AlertMessage><AlertIcon><FiBell/></AlertIcon><span><strong>¿Querés enterarte cuando aparezca algo nuevo?</strong><small>Guardá esta búsqueda y seguí sus novedades.</small></span></AlertMessage>
          <button type="button" onClick={saveAlert}>{alertState || 'Crear alerta'}</button>
        </AlertBar>
      </AlertSection>
      <PropertyList filters={filters} search={search} onFilterChange={handleChange} />
    </Page>
  );
}

const Page = styled.main`
  min-height: 100vh;
  background: #f7f5ef;
`;
const AlertSection=styled.div`
  padding:14px clamp(18px,3vw,48px) 0;background:#f7f5ef;
  @media(max-width:780px){padding:12px 14px 0;}
`;
const AlertBar=styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:24px;max-width:1180px;margin:0 auto;padding:15px 16px 15px 18px;border:1px solid rgba(255,255,255,.1);border-radius:18px;background:#294f58;color:#fff;box-shadow:0 12px 30px rgba(35,67,74,.12);
  button{min-height:44px;padding:0 21px;border:0;border-radius:999px;background:#cf7653;color:#fff;font-weight:800;white-space:nowrap;cursor:pointer;transition:transform .18s ease,background .18s ease;}button:hover{background:#bd6544;transform:translateY(-1px);}
  @media(max-width:650px){align-items:stretch;flex-direction:column;gap:14px;padding:16px;border-radius:16px;button{width:100%;}}
`;
const AlertMessage=styled.div`
  display:flex;align-items:center;gap:13px;min-width:0;span{min-width:0;}strong,small{display:block;}strong{font-size:14px;line-height:1.35;}small{margin-top:3px;color:#cbd8d7;font-size:12px;line-height:1.4;}
`;
const AlertIcon=styled.span`
  display:grid;flex:0 0 40px;width:40px;height:40px;place-items:center;border:1px solid rgba(255,255,255,.13);border-radius:12px;background:rgba(255,255,255,.08);svg{font-size:19px;}
`;
